/**
 * THE REVIEW STUDIO'S URL CONTRACT. A 3-pane workspace over one declaration,
 * addressed by which Verordnung is active and (inside it) which statement the
 * reviewer is looking at. Old Studio V1 links (`step=a|b|c|d`, `art`, `guided`)
 * still open the page: only their `stmt` is read.
 *
 *   ?sfw=<slug>             the active Verordnung (framework slug) — the
 *                           navigator's selected row and the review mask's
 *                           subject. Absent ⇒ the first framework in display
 *                           order (a hook concern; this module has no order).
 *   &stmt=<statementId>     the addressed statement inside that Verordnung —
 *                           a unit card to scroll to / expand. Absent ⇒ none
 *                           addressed, the mask shows every unit.
 *   &step=1|2|3             the open worklist step a jump named (W5-4):
 *                           step 2 also opens the article picker, step 3 the
 *                           substances editor, on the addressed card. Absent
 *                           ⇒ no step, the addressed card only expands.
 *   &zoom=1|1.25|1.5|2      the document pane's zoom level. Absent ⇒ 1 (Fit
 *                           is out of this enum — the prototype's Fit/125/
 *                           150/200% maps onto 1/1.25/1.5/2).
 *   &at=doc|mask            which pane a narrow viewport shows: the document
 *                           or the review mask. Absent ⇒ mask — the reviewer's
 *                           working surface, the document being the reference.
 *   &focus=1                focus mode is on. Absent or any other value ⇒
 *                           false.
 *
 * Parse tolerantly (an unknown or malformed value is the default, never an
 * error — a stale or hand-edited link opens the workspace rather than
 * breaking it); serialize minimally (a default value is omitted, so an
 * ordinary visit's link says only what differs from the workspace's start).
 */

import { listSearchString } from "../../listParams"

// Fit (1) is the default; 0.5 and 0.75 zoom OUT below it. The viewer sizes pages and
// its page-fraction quote boxes from `width * scale`, so any positive scale is safe.
export const ZOOM_LEVELS = [0.5, 0.75, 1, 1.25, 1.5, 2] as const
export type Zoom = (typeof ZOOM_LEVELS)[number]

export type AtPane = "doc" | "mask"
const AT_PANES: AtPane[] = ["doc", "mask"]

const DEFAULT_ZOOM: Zoom = 1
const DEFAULT_AT: AtPane = "mask"

export const STEPS = [1, 2, 3] as const
export type Step = (typeof STEPS)[number]

export interface StudioV2UrlState {
  /** `&sfw=` — the active Verordnung (framework slug), or null for "unset". */
  framework: string | null
  /** `&stmt=` — the addressed statement id, or null for "none addressed". */
  statementId: string | null
  /** `&step=`: the open worklist step a jump named (W5-4), or null for "no
   *  step" (the addressed card only expands). */
  step: Step | null
  zoom: Zoom
  at: AtPane
  /** `&focus=1`: focus mode is on. Absent or any other value means false. */
  focus: boolean
}

/** The state a fresh workspace entry carries — nothing addressed, defaults
 *  everywhere. Every real address is spread onto this rather than built from
 *  scratch, so a param the contract grows later is never silently missing. */
export const DEFAULT_STUDIO_V2_URL: StudioV2UrlState = {
  framework: null,
  statementId: null,
  step: null,
  zoom: DEFAULT_ZOOM,
  at: DEFAULT_AT,
  focus: false,
}

/** Read the contract off a query string. */
export function parseStudioV2Url(params: URLSearchParams): StudioV2UrlState {
  const rawZoom = params.get("zoom")
  const parsedZoom = rawZoom ? Number(rawZoom) : NaN
  const zoom = (ZOOM_LEVELS as readonly number[]).includes(parsedZoom)
    ? (parsedZoom as Zoom)
    : DEFAULT_ZOOM
  const rawAt = params.get("at")
  const at = (AT_PANES as string[]).includes(rawAt ?? "")
    ? (rawAt as AtPane)
    : DEFAULT_AT
  const rawStep = params.get("step")
  const parsedStep = rawStep ? Number(rawStep) : NaN
  const step = (STEPS as readonly number[]).includes(parsedStep)
    ? (parsedStep as Step)
    : null
  return {
    framework: params.get("sfw") || null,
    statementId: params.get("stmt") || null,
    step,
    zoom,
    at,
    focus: params.get("focus") === "1",
  }
}

/** Write the contract back. Only non-default values are emitted. */
export function serializeStudioV2Url(s: StudioV2UrlState): URLSearchParams {
  const p = new URLSearchParams()
  if (s.framework) p.set("sfw", s.framework)
  if (s.statementId) p.set("stmt", s.statementId)
  if (s.step) p.set("step", String(s.step))
  if (s.zoom !== DEFAULT_ZOOM) p.set("zoom", String(s.zoom))
  if (s.at !== DEFAULT_AT) p.set("at", s.at)
  if (s.focus) p.set("focus", "1")
  return p
}

/**
 * `state` with `part` applied. Switching from one Verordnung to another drops
 * the addressed statement and step unless the patch names them: they belong to
 * the Verordnung that was left, and left in the URL `stmt` pointed at another
 * regulation's statement. The first write of `sfw` (no previous one) keeps
 * them, so a deep link's `stmt` survives the hook that fills `sfw` in; the
 * same hook names them when it corrects an `sfw` the declaration lacks.
 */
export function applyStudioV2Patch(
  state: StudioV2UrlState,
  part: Partial<StudioV2UrlState>,
): StudioV2UrlState {
  const next = { ...state, ...part }
  const switched =
    state.framework !== null &&
    part.framework !== undefined &&
    part.framework !== state.framework
  if (switched) {
    if (!("statementId" in part)) next.statementId = null
    if (!("step" in part)) next.step = null
  }
  return next
}

/** `?sfw=...` — the search string for a workspace state, ready for a router
 *  push (mirrors v1's `flowSearch`). An all-default state serializes to the
 *  EMPTY string, not a bare "?" — the contract says defaults are omitted,
 *  and a trailing "?" would make `location.search` non-empty for "no query"
 *  (Copilot review, PR #2001). */
export const studioV2Search = (s: StudioV2UrlState): string => {
  const q = serializeStudioV2Url(s).toString()
  return q ? `?${q}` : ""
}

/**
 * Like `studioV2Search`, but keeps every FOREIGN param already on the URL
 * (TUR-1211) — the overview's own list filter/sort keys, which travel into
 * the review route on the initial navigate (`DeclarationsModule`'s onReview)
 * and must survive every in-place patch this contract makes afterwards
 * (switching the active Verordnung, zoom, …). `useStudioV2Url.patch` used to
 * rebuild the search string from ONLY the four studio keys, which wiped the
 * list params on the very first patch. Only this contract's own four keys
 * are ever replaced; anything else on the current URL passes through
 * untouched.
 */
export function mergeStudioV2Search(
  current: URLSearchParams,
  s: StudioV2UrlState,
): string {
  const merged = new URLSearchParams(current)
  merged.delete("sfw")
  merged.delete("stmt")
  merged.delete("step")
  merged.delete("zoom")
  merged.delete("at")
  merged.delete("focus")
  for (const [key, value] of serializeStudioV2Url(s)) {
    merged.set(key, value)
  }
  const qs = merged.toString()
  return qs ? `?${qs}` : ""
}

/** A "next declaration" jump (Skip, and `StudioV2Layout`'s own post-approve/
 *  already-approved/`onNext` jumps) carries the overview's own list filters
 *  (`listSearchString`) plus `focus=1` when the current URL has it (design
 *  spec "Flow": focus mode survives across declarations), composed via the
 *  param helpers above, never a hand-built query string. `listOnly` starts
 *  from ONLY the whitelisted list keys (not the raw current params): a
 *  studio-only key (`sfw`/`stmt`/`step`/`zoom`/`at`) from THIS declaration
 *  means nothing on the next one. Shared by `StudioV2FooterBar` and
 *  `StudioV2Layout` rather than duplicated per call site. */
export function nextDeclarationSearch(currentParams: URLSearchParams): string {
  const listOnly = new URLSearchParams(listSearchString(currentParams))
  return mergeStudioV2Search(listOnly, {
    ...DEFAULT_STUDIO_V2_URL,
    focus: parseStudioV2Url(currentParams).focus,
  })
}
